import React, { useMemo } from "react";
import type { NextPage } from "next";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import Link from "next/link";
// import AppSwitcher from "./app-Switcher";
import { getReconBreadcrumbLabel } from "../constants/reconBreadcrumbs";
import { useLayout } from "../contexts/LayoutContext";
import { appendReconQueryToBreadcrumbPath } from "../utils/reconQueryParams";

export type NavbarlandingType = {
  className?: string;
  isGstSessionExpired?: boolean;
};

const Navbar: NextPage<NavbarlandingType> = ({
  className = "",
  isGstSessionExpired = false,
}) => {
  const pathname = usePathname() || "";
  const router = useRouter();
  const { showReconDashboard } = useLayout();

  const generateBreadcrumbs = useMemo(() => {
    if (pathname === "/") {
      return [{ label: "", path: "/" }];
    }

    const segments = pathname.split("/").filter(Boolean);
    const reconType = router.query.type;

    // Route namespaces that have no standalone page — omit from breadcrumb trail
    const hiddenNamespacePaths = new Set(["/control-center"]);

    // Create array of breadcrumb items with formatted labels and cumulative paths
    const allCrumbs = segments.map((segment, index) => {
      // Build the path up to this point
      let path = `/${segments.slice(0, index + 1).join("/")}`;

      // If we're in the recon section, preserve query params on breadcrumb links
      if (pathname.startsWith("/recon") && segments[0] === "recon") {
        path = appendReconQueryToBreadcrumbPath(path, router.query);
      }

      // Decode URI component for display
      const decodedSegment = decodeURIComponent(segment);

      // Special handling for recon routes - use type-based breadcrumb labels
      let label: string;
      if (pathname.startsWith("/recon") && segment === "recon" && reconType) {
        // For the "recon" segment: type-based breadcrumb label (e.g. "Ledger Reconciliations")
        label = getReconBreadcrumbLabel(reconType, false);
      } else if (segment === "review-portal") {
        label = "Watch Tower";
      } else if (
        pathname.includes("reconciliations") ||
        (pathname.includes("recon") && index > 0)
      ) {
        // For other recon segments (after "recon") or reconciliations, use decoded segment as-is
        label = decodedSegment;
      } else {
        // Default formatting for other routes
        label = decodedSegment
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }

      return { label, path };
    });

    return [
      { label: "", path: "/" },
      ...allCrumbs.filter((crumb) => !hiddenNamespacePaths.has(crumb.path)),
    ];
  }, [pathname, router.query.type, router.query.id, showReconDashboard]);

  const breadcrumbs = generateBreadcrumbs;

  return (
    <header
      className={`self-stretch border-black-10 border-b-[1px] border-solid flex flex-row items-center justify-between pt-4 px-6 pb-4 sticky top-0 z-[100] bg-white ${className}`}
    >
      <div className="flex flex-row items-center gap-2 py-2">
        {/* <Image src="/dashboardleft.svg" alt="Logo" width={30} height={30} /> */}
        <div className="flex flex-row items-center justify-center gap-4">
         <Link href="/ledger-recon-dashboard">
          <Image
              src="/onecaplogo.svg"
              alt="OneCap Logo"
              width={120}
              height={20}
              priority
              className="object-contain w-[90%] h-[25%]"
            />
         </Link>
        </div>
        <nav aria-label="Breadcrumb" className="flex items-center">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              <Link
                href={crumb.path}
                className={`font-lato text-[1.125rem] leading-[1.25rem] no-underline -mt-0.5 ${
                  index === 1
                    ? "text-[1.25rem] italic font-bold text-[#01187D] font-lato" // First text item (index 1)
                    : index === breadcrumbs.length - 1
                    ? "font-medium text-gray-800" // Current page (last item)
                    : "font-normal text-black-40 hover:text-black-60" // Parent pages
                }`}
              >
                {crumb.label}
              </Link>
              {index < breadcrumbs.length - 1 && index !== 0 && (
                <span className="font-lato font-normal text-[1.125rem] leading-[1.25rem] text-black-40 mx-2">
                  /
                </span>
              )}
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Right side content (currently empty, reserved for future controls) */}
      <div className="flex flex-row items-center gap-4" />
    </header>
  );
};

export default Navbar;
